import { useEffect, useState } from 'react';
import {
  Modal,
  Input,
  Button,
  List,
  Empty,
  Spin,
  Avatar,
  Space,
  message,
  Divider,
} from 'antd';
import { PhoneOutlined, UserOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../lib/api-client';

interface CallNote {
  id: number;
  notes: string;
  createdAt: string;
  staff: {
    id: number;
    fullName: string | null;
    username: string;
  };
}

interface CallNotesModalProps {
  open: boolean;
  userId: number | null;
  userName?: string;
  userPhone?: string;
  /** Current operator-assigned color tag — `null` means default/none. */
  currentColor?: 'red' | 'green' | 'blue' | 'yellow' | 'gray' | 'black' | null;
  onClose: () => void;
  /** Called after a new note is saved or color changed so the parent can refresh state. */
  onChanged?: () => void;
}

// Operator-facing labels are in Uzbek (Latin). Colour mapping per ops spec:
//   Qizil (red)      → Ko'tarmadi               — didn't pick up
//   Sabzirang (yel.) → Keyinroq aloqa           — call back later
//   Ko'k (blue)      → Oxirigacha bitmagan      — incomplete / drop-off
//   Yashil (green)   → Tasdiqlangan             — confirmed
//   Qora (black)     → Qora ro'yxat             — blacklist / problematic caller
//   Gray             → "X" reset to no tag
const COLOR_OPTIONS: Array<{
  value: 'red' | 'green' | 'blue' | 'yellow' | 'gray' | 'black';
  hex: string;
  label: string;
}> = [
  { value: 'gray', hex: '#BDC3C7', label: 'Без метки' },
  { value: 'red', hex: '#EF4444', label: "Ko'tarmadi" },
  { value: 'yellow', hex: '#F59E0B', label: "Keyinroq aloqa" },
  { value: 'blue', hex: '#3B82F6', label: "Oxirigacha bitmagan" },
  { value: 'green', hex: '#10B981', label: 'Tasdiqlangan' },
  { value: 'black', hex: '#1F2937', label: "Qora ro'yxat" },
];

/**
 * Reusable modal for viewing + adding operator call-log notes for any user
 * (driver or passenger). Used from the User/Passenger/Driver detail pages
 * and the corresponding list pages.
 */
export const CallNotesModal: React.FC<CallNotesModalProps> = ({
  open,
  userId,
  userName,
  userPhone,
  currentColor,
  onClose,
  onChanged,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState<CallNote[]>([]);
  const [draft, setDraft] = useState('');
  const [colorSaving, setColorSaving] = useState(false);
  const [color, setColor] = useState<typeof currentColor>(currentColor ?? null);

  useEffect(() => {
    setColor(currentColor ?? null);
  }, [currentColor]);

  const saveColor = async (next: 'red' | 'green' | 'blue' | 'yellow' | 'gray' | 'black' | null) => {
    if (!userId) return;
    const previous = color;
    setColor(next);
    setColorSaving(true);
    try {
      await apiClient.put(`/admin/users/${userId}/call-icon-color`, {
        color: next,
      });
      message.success('Цвет метки сохранён');
      onChanged?.();
    } catch (err: any) {
      // Revert on failure.
      setColor(previous ?? null);
      message.error(
        err?.response?.data?.message || 'Не удалось сохранить цвет',
      );
    } finally {
      setColorSaving(false);
    }
  };

  const fetchNotes = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/admin/users/${userId}/call-notes`);
      setNotes(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || 'Не удалось загрузить заметки',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && userId) {
      setDraft('');
      fetchNotes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const saveNote = async () => {
    if (!userId) return;
    const trimmed = draft.trim();
    if (!trimmed) {
      message.warning('Введите заметки звонка');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post(`/admin/users/${userId}/call-notes`, {
        notes: trimmed,
      });
      message.success('Заметка сохранена');
      setDraft('');
      await fetchNotes();
      onChanged?.();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || 'Не удалось сохранить заметку',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <PhoneOutlined style={{ color: '#10B981' }} />
          <span>
            Заметки звонков
            {userName ? ` — ${userName}` : ''}
            {userPhone ? ` (${userPhone})` : ''}
          </span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
    >
      {/* Color tag picker */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            fontWeight: 500,
            fontSize: 13,
            marginBottom: 6,
            color: '#2C3E50',
          }}
        >
          Метка для списка
        </div>
        <Space wrap size={8}>
          {COLOR_OPTIONS.map((opt) => {
            const selected = (color ?? null) === (opt.value === 'gray' ? null : opt.value);
            // 'gray' acts as the "no tag" reset — store as null on save.
            const handleClick = () =>
              saveColor(opt.value === 'gray' ? null : opt.value);
            // The default selected-ring colour matches the black swatch, so
            // for the black option we invert it (white ring) to keep the
            // selection state visible.
            const selectedBorderColor = opt.value === 'black' ? '#FFFFFF' : '#1F2937';
            return (
              <button
                key={opt.value}
                type="button"
                onClick={handleClick}
                disabled={colorSaving}
                title={opt.label}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: selected
                    ? `3px solid ${selectedBorderColor}`
                    : '2px solid #E5E7EB',
                  // Outer ring keeps the black swatch visible against a white modal.
                  boxShadow:
                    opt.value === 'black' && selected
                      ? '0 0 0 2px #1F2937'
                      : undefined,
                  backgroundColor: opt.hex,
                  cursor: colorSaving ? 'not-allowed' : 'pointer',
                  outline: 'none',
                  padding: 0,
                  transition: 'border 0.15s, box-shadow 0.15s',
                }}
              >
                {opt.value === 'gray' && (
                  <span
                    style={{
                      color: '#fff',
                      fontSize: 18,
                      lineHeight: '24px',
                    }}
                  >
                    ×
                  </span>
                )}
              </button>
            );
          })}
        </Space>
        <div style={{ marginTop: 6, color: '#7F8C8D', fontSize: 12 }}>
          {color
            ? COLOR_OPTIONS.find(
                (o) => o.value === color,
              )?.label ?? ''
            : 'Цвет иконки в списке: по умолчанию (серый/зелёный)'}
        </div>
      </div>

      <Divider style={{ margin: '8px 0 16px' }} />

      {/* New-note form */}
      <div style={{ marginBottom: 8 }}>
        <Input.TextArea
          placeholder="Что обсудили со звонящим? (тема, договорённости, проблема...)"
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={2000}
          showCount
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={saveNote}
            loading={saving}
            disabled={!draft.trim()}
          >
            Сохранить заметку
          </Button>
        </div>
      </div>

      <Divider style={{ margin: '16px 0' }}>История звонков</Divider>

      {/* History list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : notes.length === 0 ? (
        <Empty description="Звонков пока не было" />
      ) : (
        <List
          dataSource={notes}
          itemLayout="horizontal"
          renderItem={(note) => (
            <List.Item key={note.id} style={{ alignItems: 'flex-start' }}>
              <List.Item.Meta
                avatar={
                  <Avatar
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#10B981' }}
                  />
                }
                title={
                  <Space size="small" wrap>
                    <strong>
                      {note.staff?.fullName || note.staff?.username || 'Operator'}
                    </strong>
                    <span style={{ color: '#95A5A6', fontSize: 12 }}>
                      {dayjs(note.createdAt).format('D MMMM, YYYY | HH:mm')}
                    </span>
                  </Space>
                }
                description={
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      color: '#2C3E50',
                      lineHeight: 1.5,
                    }}
                  >
                    {note.notes}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
};
